// Kampagnen & Angles (Google Ads): Kampagnen-Summenzeilen + Anzeigengruppen-
// Zeilen (Nicos Konvention: 1 Anzeigengruppe = 1 Angle). Zeigt Ads-Klicks und
// Kosten auch dann, wenn das First-Party-Tracking (noch) keine Besucher kennt —
// z. B. weil das t.js-Snippet jünger ist als die Kampagne. Admins können
// fremde/alte Kampagnen aus der LMS-Auswertung ausschließen (Migration 037).

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { ZeilenAktionen, useAdsSteuerung } from '@/components/analytics/AdsSteuerung';
import {
  channelLabels,
  excludeCampaign,
  includeCampaign,
  kampagnenOptimierung,
  syncAdsJetzt,
  type CampaignStat,
  type Channel,
  type ExcludedCampaign,
} from '@/lib/analytics-api';

interface Props {
  campaigns: CampaignStat[];
  excluded?: ExcludedCampaign[];
  /** Nur mit Projekt-Scope + Admin lassen sich Kampagnen ausschließen. */
  projectId?: string | null;
  isAdmin?: boolean;
  onChanged?: () => void;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

function euroFmt(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function StatusBadge({ status, hinweis }: { status: string | null | undefined; hinweis?: string | null }) {
  // Aktiv, aber Google liefert (noch) nicht aus — typisch direkt nach dem
  // Go-live: „Anzeigen in Prüfung". Ohne dieses Badge sieht eine frisch
  // aktivierte Kampagne aus wie eine ignorierte.
  if (status === 'ENABLED' && hinweis) {
    return (
      <span
        className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium align-middle"
        title={hinweis}
      >
        ⏳ {hinweis.length > 42 ? `${hinweis.slice(0, 42)}…` : hinweis}
      </span>
    );
  }
  if (!status || status === 'ENABLED') return null;
  const label = status === 'PAUSED' ? 'Pausiert' : status;
  return (
    <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200 text-[10px] font-medium align-middle">
      {label}
    </span>
  );
}

export default function CampaignsTable({ campaigns, excluded, projectId, isAdmin, onChanged }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  // Steuerung direkt aus der Tabelle: pausieren, Gebotsstrategie, Budget —
  // jeweils mit Begründung, die als Maßnahme in den Monatsbericht läuft.
  const { dialog: steuerDialog, starten: steuerungStarten } = useAdsSteuerung(projectId, onChanged);
  // „Optimierungspotenzial": Nicos wöchentliche Runde auf Abruf für EINE
  // Kampagne — Suchbegriffe prüfen, ausschließen, einbuchen, Unterseiten-
  // Themen erkennen. Ergebnis erscheint direkt hier, die umsetzbaren
  // Vorschläge zusätzlich mit GO-Button im Empfehlungen-Tab.
  const [optiErgebnis, setOptiErgebnis] = useState<{
    kampagne: string;
    zusammenfassung: string;
    vorschlaege: Array<{ art: string; target: string; titel: string; begruendung: string }>;
    hinweis: string | null;
  } | null>(null);
  const optiMut = useMutation({
    mutationFn: (kampagne: string) => kampagnenOptimierung({ project_id: projectId!, campaign: kampagne }),
    onSuccess: (r, kampagne) => {
      setOptiErgebnis({ kampagne, zusammenfassung: r.zusammenfassung, vorschlaege: r.vorschlaege, hinweis: r.hinweis });
      toast.success(`${r.vorschlaege.length} Vorschläge für „${kampagne}"`);
    },
    onError: (e: any) => toast.error(e?.message || 'Analyse fehlgeschlagen'),
  });
  const [showExcluded, setShowExcluded] = useState(false);
  const canManage = !!(isAdmin && projectId);
  // Google-Ads-Sync von Hand auslösen. Der nächtliche Lauf ersetzt die Daten
  // komplett — wer eine Kampagne gerade gelöscht hat, sieht sie sonst bis zum
  // nächsten Morgen weiter im Dashboard.
  const syncMut = useMutation({
    mutationFn: () => syncAdsJetzt(),
    onSuccess: () => { toast.success('Google-Ads-Daten aktualisiert'); onChanged?.(); },
    onError: (e: any) => toast.error(e?.message || 'Aktualisieren fehlgeschlagen'),
  });
  if (!campaigns.length && !(excluded?.length && canManage)) return null;

  const hasCost = campaigns.some((c) => c.cost_eur != null && c.cost_eur > 0);
  const hasAdsClicks = campaigns.some((c) => c.ads_clicks != null);

  const doExclude = async (c: CampaignStat) => {
    if (!projectId || busy) return;
    if (!window.confirm(`Kampagne „${c.utm_campaign}" aus der LMS-Auswertung ausschließen?\n\nSie verschwindet aus diesem Panel, dem Monats-Report und den KI-Empfehlungen (in Google Ads ändert sich nichts).`)) return;
    setBusy(c.campaign_id || c.utm_campaign || '');
    try {
      await excludeCampaign({ projectId, campaignId: c.campaign_id, campaign: c.utm_campaign });
      onChanged?.();
    } catch (e: any) {
      alert(e?.message || 'Ausschließen fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  const doInclude = async (e: ExcludedCampaign) => {
    const pid = e.project_id || projectId;
    if (!pid || busy) return;
    setBusy(e.campaign_id || e.utm_campaign || '');
    try {
      await includeCampaign({ projectId: pid, campaignId: e.campaign_id, campaign: e.utm_campaign });
      onChanged?.();
    } catch (err: any) {
      alert(err?.message || 'Aufheben fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {steuerDialog}
      {optiErgebnis && (
        <div className="px-4 py-3 border-b border-amber-100 bg-amber-50/50">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-500" /> Optimierungspotenzial: {optiErgebnis.kampagne}
              </h3>
              {optiErgebnis.zusammenfassung && (
                <p className="text-xs text-gray-600 mt-1">{optiErgebnis.zusammenfassung}</p>
              )}
            </div>
            <button onClick={() => setOptiErgebnis(null)} className="text-xs text-gray-400 hover:text-gray-700 shrink-0">
              Schließen
            </button>
          </div>
          {optiErgebnis.vorschlaege.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {optiErgebnis.vorschlaege.map((v, i) => (
                <li key={i} className="text-xs">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-1.5 align-middle ${
                    v.art === 'negative_keyword' ? 'bg-red-100 text-red-700'
                    : v.art === 'keyword_neu' ? 'bg-green-100 text-green-700'
                    : v.target === 'website' ? 'bg-violet-100 text-violet-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                    {v.art === 'negative_keyword' ? 'Ausschließen' : v.art === 'keyword_neu' ? 'Einbuchen' : v.target === 'website' ? 'Unterseite' : 'Beobachtung'}
                  </span>
                  <span className="text-gray-800">{v.titel}</span>
                  <span className="text-gray-400"> — {v.begruendung}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-gray-500">Keine Auffälligkeiten — die Kampagne ist sauber, weiterlaufen lassen.</p>
          )}
          {optiErgebnis.hinweis && <p className="mt-2 text-[11px] text-amber-700">{optiErgebnis.hinweis}</p>}
        </div>
      )}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Kampagnen &amp; Angles</h2>
          {isAdmin && (
            <button
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              title="Holt Kampagnen, Klicks und Kosten sofort aus Google Ads. Läuft sonst nur nachts um 03:30 UTC."
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60"
            >
              {syncMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {syncMut.isPending ? 'Hole Daten…' : 'Jetzt aktualisieren'}
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Was im Google-Ads-Konto wirklich läuft. Fette Zeile = Kampagne, eingerückt darunter = Anzeigengruppe
          (bei uns ein Angle je Gruppe) — eingerückte Zeilen erscheinen ab zwei Gruppen.
          Klicks &amp; Kosten kommen täglich aus Google Ads, Besucher &amp; Kontakte aus dem eigenen Tracking
          (Formulare plus Telefon-/Mail-Klicks).
          Ganz unten sammelt „Ohne Kampagne“ die Aufrufe einer Angle-URL, die über keine Anzeige kamen
          (Vorschau, geteilter Link).
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
              <th className="px-4 py-2 font-medium">Kampagne</th>
              <th className="px-4 py-2 font-medium">Angle / Anzeigengruppe</th>
              <th className="px-4 py-2 font-medium">Kanal</th>
              {hasAdsClicks && <th className="px-4 py-2 font-medium text-right">Ads-Klicks</th>}
              <th className="px-4 py-2 font-medium text-right">Besucher</th>
              <th
                className="px-4 py-2 font-medium text-right"
                title={'Formular-Anfragen plus Klicks auf Telefonnummer/E-Mail, die dieser Kampagne zugeordnet werden konnten.'}
              >
                Kontakte
              </th>
              <th className="px-4 py-2 font-medium text-right">Konversion</th>
              {hasCost && <th className="px-4 py-2 font-medium text-right">Kosten</th>}
              {hasCost && <th className="px-4 py-2 font-medium text-right">€ / Kontakt</th>}
              {canManage && <th className="px-2 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {campaigns.map((c, i) => {
              const isTotal = c.is_total !== false; // Alt-Daten ohne Flag wie Summenzeilen behandeln
              return (
                <tr key={`${c.campaign_id || c.utm_campaign}-${c.ad_group_id || c.angle}-${i}`} className="hover:bg-gray-50">
                  <td className={`px-4 py-2 max-w-[240px] truncate ${isTotal ? 'text-gray-900 font-medium' : 'text-gray-300'}`} title={c.utm_campaign || undefined}>
                    {isTotal ? (
                      <>
                        {c.utm_campaign || <span className="text-gray-300">–</span>}
                        <StatusBadge status={c.status} hinweis={c.auslieferung_hinweis} />
                      </>
                    ) : (
                      '↳'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {!isTotal && c.angle ? (
                      <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">{c.angle}</span>
                    ) : (
                      <span className="text-gray-300">–</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">{isTotal ? channelLabels[c.channel as Channel] || c.channel : ''}</td>
                  {hasAdsClicks && (
                    <td className={`px-4 py-2 text-right tabular-nums ${isTotal ? 'text-gray-800' : 'text-gray-500'}`}>
                      {c.ads_clicks != null ? fmt(c.ads_clicks) : '–'}
                    </td>
                  )}
                  <td className={`px-4 py-2 text-right tabular-nums ${isTotal ? '' : 'text-gray-500'}`}>{fmt(c.visitors)}</td>
                  <td
                    className={`px-4 py-2 text-right tabular-nums ${isTotal ? '' : 'text-gray-500'}`}
                    title={(c.contact_clicks ?? 0) > 0 ? `${c.leads} über Formular, ${c.contact_clicks} Tel./Mail-Klicks` : undefined}
                  >
                    {c.kontakte ?? c.leads}
                    {/* Anrufe direkt aus Anzeige/Maps (Google-Messung): eigener
                        Kanal NEBEN den Website-Kontakten — nie einrechnen, aber
                        sichtbar machen, sonst wirkt eine PMax grundlos. */}
                    {isTotal && (c.ads_anrufe_30d ?? 0) > 0 && (
                      <span
                        className="ml-1.5 inline-flex items-center rounded bg-violet-50 px-1 py-0.5 text-[10px] font-medium text-violet-700"
                        title={`+ ${c.ads_anrufe_30d} Anrufe direkt aus Anzeige/Maps (30 Tage, von Google gemessen — laufen nicht über die Website, deshalb nicht in der Kontakte-Zahl)`}
                      >
                        +{c.ads_anrufe_30d} ☎
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {c.visitors > 0 ? `${(c.conversion_rate * 100).toFixed(1)} %` : '–'}
                  </td>
                  {hasCost && (
                    <td className={`px-4 py-2 text-right tabular-nums ${isTotal ? 'text-gray-700' : 'text-gray-500'}`}>
                      {c.cost_eur != null && c.cost_eur > 0 ? euroFmt(c.cost_eur) : '–'}
                    </td>
                  )}
                  {hasCost && (
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                      {c.cost_eur != null && c.cost_eur > 0 && (c.kontakte ?? c.leads) > 0 ? euroFmt(c.cost_eur / (c.kontakte ?? c.leads)) : '–'}
                    </td>
                  )}
                  {canManage && (
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        {isTotal && c.utm_campaign && !c.utm_campaign.startsWith('Ohne Kampagne') && (
                          <button
                            onClick={() => optiMut.mutate(c.utm_campaign!)}
                            disabled={optiMut.isPending}
                            title="Optimierungspotenzial der Kampagne: echte Suchbegriffe prüfen — ausschließen, einbuchen, Unterseiten-Themen erkennen (Nicos Wochenrunde auf Abruf)"
                            className="p-1 text-amber-400 hover:text-amber-600 transition disabled:opacity-40"
                          >
                            {optiMut.isPending && optiMut.variables === c.utm_campaign ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Sparkles className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        <ZeilenAktionen row={c} starten={steuerungStarten} />
                        {isTotal && (c.campaign_id || c.utm_campaign) && (
                          <button
                            onClick={() => doExclude(c)}
                            disabled={!!busy}
                            title="Kampagne aus der LMS-Auswertung ausschließen (Panel, Report, KI) — ändert nichts im Ads-Konto"
                            className="text-gray-300 hover:text-red-500 text-xs px-1"
                          >
                            ⊘
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canManage && !!excluded?.length && (
        <div className="border-t border-gray-100 px-4 py-2">
          <button
            onClick={() => setShowExcluded((v) => !v)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            {showExcluded ? '▾' : '▸'} Ausgeschlossene Kampagnen ({excluded.length})
          </button>
          {showExcluded && (
            <ul className="mt-2 space-y-1">
              {excluded.map((e, i) => (
                <li key={`${e.campaign_id || e.utm_campaign}-${i}`} className="flex items-center justify-between text-xs text-gray-500">
                  <span className="truncate max-w-[420px]" title={e.utm_campaign || undefined}>
                    {e.utm_campaign || e.campaign_id}
                    {e.status === 'PAUSED' && <span className="text-gray-400"> · pausiert</span>}
                    {e.reason && <span className="text-gray-400"> · {e.reason}</span>}
                  </span>
                  <button
                    onClick={() => doInclude(e)}
                    disabled={!!busy}
                    className="text-blue-500 hover:text-blue-700 ml-3 shrink-0"
                  >
                    wieder aufnehmen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
